import { forwardRef, useImperativeHandle, useRef } from 'react';
import { View } from 'react-native';

import { Button, Heading, Text, VStack } from '@squire/ui';

import {
  BottomSheet,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { DeleteServiceConfirmationBottomSheetProps } from './types';

export const DeleteServiceConfirmationBottomSheet = forwardRef<
  BottomSheetRef,
  DeleteServiceConfirmationBottomSheetProps
>(({ serviceName, addonNames, onConfirm, onDismiss }, ref) => {
  const { t } = useScreenTranslations('DeleteServiceConfirmationBottomSheet');
  const bottomSheetRef = useRef<BottomSheetRef>(null);

  useImperativeHandle(ref, () => bottomSheetRef.current as BottomSheetRef);

  const handleConfirm = () => {
    bottomSheetRef.current?.close();
    onConfirm();
  };

  const handleDismiss = () => {
    bottomSheetRef.current?.close();
    onDismiss();
  };

  return (
    <BottomSheet
      ref={bottomSheetRef}
      scrollable={false}
      accessibilityLabel={serviceName}
    >
      <VStack style={styles.content}>
        <Heading variant="m">{serviceName}</Heading>
        <Text variant="m" color="textSecondary">
          {t('removeService.subtitle')}
        </Text>
        <View style={styles.addonList}>
          {addonNames.map((name) => (
            <View key={name} style={styles.addonItem}>
              <Text variant="s" color="textSecondary">
                {name}
              </Text>
            </View>
          ))}
        </View>
        <VStack style={styles.buttonsContainer}>
          <Button
            onPress={handleConfirm}
            style={styles.confirmButton}
            testID="delete-service-confirm-button"
          >
            {t('removeService.confirm')}
          </Button>
          <Button
            variant="secondary"
            onPress={handleDismiss}
            testID="delete-service-dismiss-button"
          >
            {t('removeService.dismiss')}
          </Button>
        </VStack>
      </VStack>
    </BottomSheet>
  );
});

DeleteServiceConfirmationBottomSheet.displayName =
  'DeleteServiceConfirmationBottomSheet';
